import { useNavigate } from 'react-router-dom';
import { TopVideo, VideoPostMarker } from '../../../shared/types/dashboard';
import PlayableVideoThumbnail from '../ui/PlayableVideoThumbnail';

interface TopVideoHighlightsProps {
  video?: TopVideo | null;
  videoPosts?: VideoPostMarker[];
  isLoading?: boolean;
  period?: string;
  username?: string;
}

const COLOR_LIKES = '#EC0452';
const COLOR_VIEWS = '#22C55E';
const COLOR_COMMENTS = '#94A3B8';
const COLOR_SHARES = '#F59E0B';
const COLOR_VIDEOS = '#14B8A6';

const formatNumber = (num: number) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toLocaleString();
};

const TopVideoHighlights = ({ video, isLoading, period = '7d', username }: TopVideoHighlightsProps) => {
  const navigate = useNavigate();
  const periodLabel = period === '24h' ? 'Last 24 hours' : 
                    period === '7d'  ? 'Last 7 days' :
                    period === '1m'  ? 'Last 30 days' :
                    period === '3m'  ? 'Last 90 days' : 'Last 6 months';

  if (isLoading) {
    return (
      <div className="bg-slate-900/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-800 animate-pulse h-64">
      </div>
    );
  }

  const featuredVideo = video; // video is compatible enough with TopVideo type, we just use it directly

  if (!featuredVideo) {
    return (
      <div className="bg-slate-900/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-800 flex flex-col h-full">
        <div className="mb-4">
            <h3 className="font-bold text-white text-lg">Top Performer</h3>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{periodLabel}</p>
        </div>
        <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-8 text-center">
          No video data available for this range
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-900/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-800 flex flex-col h-full">
        <div className="mb-4">
            <h3 className="font-bold text-white text-lg">Top Performer</h3>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{periodLabel}</p>
        </div>
        
        <div className="flex-1 flex gap-4">
            {/* Thumbnail - click to play TikTok video */}
            <PlayableVideoThumbnail
                coverImageUrl={featuredVideo.cover_image_url}
                title={featuredVideo.description || 'Video Thumbnail'}
                tiktokId={featuredVideo.tiktok_id}
                username={username}
                videoId={featuredVideo.video_id}
                stopPropagation
            />

            <div className="flex-1 py-0.5">
                <div className="flex items-start justify-between mb-2">
                    <h4 
                        className="font-medium text-white line-clamp-2 text-sm leading-snug cursor-pointer hover:text-primary-400 transition-colors"
                        onClick={() => {
                          if (username && featuredVideo.video_id) {
                            navigate(`/dashboard/${username}/video/${featuredVideo.video_id}`);
                          }
                        }}
                    >
                       {featuredVideo.description || 'No description'}
                    </h4>
                </div>
                
                <div className="space-y-1.5">
                    <div className="flex items-center text-xs text-slate-400">
                      <svg className="w-3.5 h-3.5 mr-2" style={{ color: COLOR_VIEWS }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      <span className="font-semibold mr-1" style={{ color: COLOR_VIEWS }}>{formatNumber(featuredVideo.view_count)}</span> Views
                    </div>
                    <div className="flex items-center text-xs text-slate-400">
                      <svg className="w-3.5 h-3.5 mr-2" style={{ color: COLOR_LIKES }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      <span className="font-semibold mr-1" style={{ color: COLOR_LIKES }}>{formatNumber(featuredVideo.like_count)}</span> Likes
                    </div>
                    <div className="flex items-center text-xs text-slate-400">
                      <svg className="w-3.5 h-3.5 mr-2" style={{ color: COLOR_COMMENTS }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      <span className="font-semibold mr-1" style={{ color: COLOR_COMMENTS }}>{formatNumber(featuredVideo.comment_count)}</span> Comments
                    </div>
                    <div className="flex items-center text-xs text-slate-400">
                      <svg className="w-3.5 h-3.5 mr-2" style={{ color: COLOR_SHARES }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      <span className="font-semibold mr-1" style={{ color: COLOR_SHARES }}>{formatNumber(featuredVideo.share_count)}</span> Shares
                    </div>
                </div>
                
                <div className="mt-3">
                     <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded border"
                      style={{
                        backgroundColor: `${COLOR_VIDEOS}1a`,
                        borderColor: `${COLOR_VIDEOS}60`,
                        color: COLOR_VIDEOS
                      }}
                     >
                        TOP PERFORMER 🔥
                     </span>
                </div>
            </div>
        </div>

         <button 
            className="w-full mt-4 py-2 text-sm font-medium text-slate-400 hover:text-white border border-slate-800 rounded-lg hover:bg-slate-800/50 transition duration-200"
            onClick={() => {
              if (username && featuredVideo.video_id) {
                navigate(`/dashboard/${username}/video/${featuredVideo.video_id}`);
              }
            }}
         >
            View Analytics Detail
        </button>
      </div>
    </>
  );
};


export default TopVideoHighlights;
